import {useState} from 'react';
import {
    Button,
    Drawer,
    DrawerProps,
    Group,
    ScrollArea,
    Select,
    Stack,
    TextInput,
} from '@mantine/core';
import {
    MdCancel,
    MdCheck,
    MdClear,
} from 'react-icons/md';

import {useDefectSignaturesFilter} from '../hooks/useDefectSignaturesFilter';
import {
    DefectSignaturesFilter,
    DefectStatus,
    VerificationResult,
} from '../types';

// Only the three fields DefectSignaturesFilterInput actually accepts. Nothing
// else may be sent — AppSync rejects unknown input fields, and a filter the
// server ignores is worse than no filter (it silently lies about what is in view).
const STATUS_OPTIONS = [
    {
        value: '',
        label: 'All triage states'
    },
    ...Object.values(DefectStatus)
             .map(value => ({
                 value,
                 label: value
             })),
];

const VERIFICATION_OPTIONS = [
    {
        value: '',
        label: 'All signal types'
    },
    ...Object.values(VerificationResult)
             .map(value => ({
                 value,
                 label: value === VerificationResult.NOT_VERIFIED
                     ? 'NOT_VERIFIED — unverified effect'
                     : value
             })),
];

export const FilterDrawer = ({
                                 opened,
                                 onClose,
                                 ...rest
                             }: DrawerProps) => {

    const {
        filters,
        handleSetFilters,
        handleClearFilters,
    } = useDefectSignaturesFilter();

    // Local draft so the list only refetches on Apply, not on every keystroke.
    const [draft, setDraft] = useState<DefectSignaturesFilter>(filters);

    const setField = (field: keyof DefectSignaturesFilter) => (value: string | null) =>
        setDraft(current => ({
            ...current,
            [field]: value ?? ''
        }));

    return (
        <Drawer
            transitionProps={{
                transition: 'slide-left',
                duration: 750,
                timingFunction: 'ease'
            }}
            position='right'
            offset={10}
            opened={opened}
            onClose={onClose}
            title='Filter Defects'
            padding='xl'
            size='md'
            scrollAreaComponent={ScrollArea.Autosize}
            {...rest}
        >
            <Stack gap='md' my='md'>
                <Select
                    label='Signal type'
                    description='NOT_VERIFIED is the high-value one: the action worked but the platform did not record its effect.'
                    data={VERIFICATION_OPTIONS}
                    value={draft.verificationResult ?? ''}
                    onChange={setField('verificationResult')}
                    clearable={false}
                />
                <Select
                    label='Triage state'
                    description='Where a human has taken it. NEW is untriaged.'
                    data={STATUS_OPTIONS}
                    value={draft.status ?? ''}
                    onChange={setField('status')}
                    clearable={false}
                />
                <TextInput
                    label='Action Code'
                    placeholder='e.g. applications.enable'
                    value={draft.actionCode ?? ''}
                    onChange={(event) => setField('actionCode')(event.currentTarget.value)}
                />
            </Stack>

            <Group justify='space-between' mt='xl'>
                <Button
                    variant='outline'
                    leftSection={<MdCancel/>}
                    onClick={onClose}
                >
                    Cancel
                </Button>
                <Group gap='xs'>
                    <Button
                        variant='outline'
                        leftSection={<MdClear/>}
                        onClick={() => {
                            setDraft({});
                            handleClearFilters();
                        }}
                    >
                        Clear
                    </Button>
                    <Button
                        leftSection={<MdCheck/>}
                        onClick={() => {
                            handleSetFilters(draft);
                            onClose();
                        }}
                    >
                        Apply
                    </Button>
                </Group>
            </Group>
        </Drawer>
    );
};
